import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Image,
  Input,
  Link,
  Select,
  SimpleGrid,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import axiosInstance from '../../../api/AxiosInstance';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';

interface Props {
  modo: 'new' | 'edit' | 'view';
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

type FormState = {
  nombre: string;
  tipo: string;
  descripcion: string;
  ubicacion: string;
  comunidad: string;
  lat: string;
  lng: string;
  costo: string;
  servicios: string;
  contacto: string;
  estatus: string;
  horarioDias: string;
  horaApertura: string;
  horaCierre: string;
  horarioNotas: string;
};

const DEFAULT_CENTER = { lat: 19.3256653, lng: -98.2568412 };

const initialForm: FormState = {
  nombre: '',
  tipo: '',
  descripcion: '',
  ubicacion: '',
  comunidad: '',
  lat: '',
  lng: '',
  costo: '',
  servicios: '',
  contacto: '',
  estatus: 'activo',
  horarioDias: '',
  horaApertura: '',
  horaCierre: '',
  horarioNotas: '',
};

const parseHorario = (horario: string | null | undefined) => {
  const raw = (horario || '').trim();
  if (!raw) return { horarioDias: '', horaApertura: '', horaCierre: '', horarioNotas: '' };

  const match = raw.match(/^([^|]+?)\s*\|\s*(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})(?:\s*\|\s*(.*))?$/);
  if (match) {
    return {
      horarioDias: match[1].trim(),
      horaApertura: match[2],
      horaCierre: match[3],
      horarioNotas: (match[4] || '').trim(),
    };
  }

  return { horarioDias: '', horaApertura: '', horaCierre: '', horarioNotas: raw };
};

const buildHorario = (form: FormState) => {
  const parts: string[] = [];
  if (form.horarioDias.trim()) parts.push(form.horarioDias.trim());
  if (form.horaApertura && form.horaCierre) parts.push(`${form.horaApertura} - ${form.horaCierre}`);
  if (form.horarioNotas.trim()) parts.push(form.horarioNotas.trim());
  return parts.join(' | ');
};

const getBaseOrigin = () => {
  const configured = axiosInstance.defaults.baseURL as string | undefined;
  if (!configured) return window.location.origin;
  try {
    return new URL(configured, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

const resolveAssetUrl = (value: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${getBaseOrigin()}${value.startsWith('/') ? '' : '/'}${value}`;
};

const isImagePath = (value: string) => /\.(png|jpe?g|webp|gif)$/i.test(value);

const FormularioAtractivo: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [fotosExistentes, setFotosExistentes] = useState<string[]>([]);
  const [fotosNuevas, setFotosNuevas] = useState<File[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);

  const { isLoaded: mapaCargado, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const disabled = modo === 'view';

  useEffect(() => {
    if (!data) {
      setForm(initialForm);
      setFotosExistentes([]);
      setFotosNuevas([]);
      setMapCenter(DEFAULT_CENTER);
      return;
    }

    const horario = parseHorario(data.horario);
    const lat = data.lat ?? '';
    const lng = data.lng ?? '';

    setForm({
      nombre: data.nombre || '',
      tipo: data.tipo || '',
      descripcion: data.descripcion || '',
      ubicacion: data.ubicacion || '',
      comunidad: data.comunidad || '',
      lat: lat === null || lat === undefined ? '' : String(lat),
      lng: lng === null || lng === undefined ? '' : String(lng),
      costo: data.costo || '',
      servicios: data.servicios || '',
      contacto: data.contacto || '',
      estatus: data.estatus || 'activo',
      ...horario,
    });

    setFotosExistentes(Array.isArray(data.fotos) ? data.fotos.filter(Boolean) : []);
    setFotosNuevas([]);

    if (lat !== '' && lng !== '' && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
      setMapCenter({ lat: Number(lat), lng: Number(lng) });
    } else {
      setMapCenter(DEFAULT_CENTER);
    }
  }, [data]);

  useEffect(() => {
    const latNum = Number(form.lat);
    const lngNum = Number(form.lng);
    if (!Number.isNaN(latNum) && !Number.isNaN(lngNum) && form.lat !== '' && form.lng !== '') {
      setMapCenter({ lat: latNum, lng: lngNum });
    }
  }, [form.lat, form.lng]);

  const markerPosition = useMemo(() => {
    const latNum = Number(form.lat);
    const lngNum = Number(form.lng);
    if (Number.isNaN(latNum) || Number.isNaN(lngNum) || form.lat === '' || form.lng === '') return null;
    return { lat: latNum, lng: lngNum };
  }, [form.lat, form.lng]);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const reverseGeocode = (lat: number, lng: number) => {
    const googleApi = (window as any).google;
    if (!googleApi?.maps?.Geocoder) return;
    const geocoder = new googleApi.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results: any, status: string) => {
      if (status === 'OK' && results?.[0]?.formatted_address) {
        setForm((prev) => ({ ...prev, ubicacion: results[0].formatted_address }));
      }
    });
  };

  const geocodeDireccion = () => {
    if (!form.ubicacion.trim()) return;
    const googleApi = (window as any).google;
    if (!googleApi?.maps?.Geocoder) return;
    const geocoder = new googleApi.maps.Geocoder();
    geocoder.geocode({ address: form.ubicacion }, (results: any, status: string) => {
      if (status !== 'OK' || !results?.[0]) return;
      const loc = results[0].geometry.location;
      const lat = Number(loc.lat()).toFixed(8);
      const lng = Number(loc.lng()).toFixed(8);
      setForm((prev) => ({ ...prev, lat, lng, ubicacion: results[0].formatted_address || prev.ubicacion }));
      setMapCenter({ lat: Number(lat), lng: Number(lng) });
    });
  };

  const usarUbicacionActual = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = Number(position.coords.latitude).toFixed(8);
        const lng = Number(position.coords.longitude).toFixed(8);
        setForm((prev) => ({ ...prev, lat, lng }));
        setMapCenter({ lat: Number(lat), lng: Number(lng) });
        reverseGeocode(Number(lat), Number(lng));
      },
      () => {}
    );
  };

  const handleMapClick = (event: any) => {
    if (disabled) return;
    const lat = event?.latLng?.lat?.();
    const lng = event?.latLng?.lng?.();
    if (typeof lat !== 'number' || typeof lng !== 'number') return;
    const latFixed = lat.toFixed(8);
    const lngFixed = lng.toFixed(8);
    setForm((prev) => ({ ...prev, lat: latFixed, lng: lngFixed }));
    setMapCenter({ lat, lng });
    reverseGeocode(lat, lng);
  };

  const handleFotosSeleccionadas = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setFotosNuevas((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removeFotoNueva = (index: number) => {
    setFotosNuevas((prev) => prev.filter((_, i) => i !== index));
  };

  const removeFotoExistente = (url: string) => {
    setFotosExistentes((prev) => prev.filter((item) => item !== url));
  };

  const handleSubmit = async () => {
    if (!form.nombre.trim() || !form.tipo.trim()) {
      setError('Nombre y tipo son obligatorios.');
      return;
    }

    setError('');
    setGuardando(true);
    try {
      const horario = buildHorario(form);
      const fd = new FormData();

      [
        ['nombre', form.nombre],
        ['tipo', form.tipo],
        ['descripcion', form.descripcion],
        ['ubicacion', form.ubicacion],
        ['comunidad', form.comunidad],
        ['lat', form.lat],
        ['lng', form.lng],
        ['horario', horario],
        ['costo', form.costo],
        ['servicios', form.servicios],
        ['contacto', form.contacto],
        ['estatus', form.estatus],
      ].forEach(([key, value]) => {
        fd.append(key, value ?? '');
      });

      fd.append('fotosExistentes', JSON.stringify(fotosExistentes));
      fotosNuevas.forEach((file) => fd.append('fotos', file));

      if (modo === 'edit' && data?.id) {
        await axiosInstance.put(`/turismo/atractivos/${data.id}`, fd);
      } else {
        await axiosInstance.post('/turismo/atractivos', fd);
      }

      onGuardado && onGuardado();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No fue posible guardar el atractivo.');
    } finally {
      setGuardando(false);
    }
  };

  const horarioPreview = buildHorario(form);

  return (
    <Box>
      <Stack spacing={5}>
        <Box borderWidth="1px" borderRadius="lg" p={4} bg="gray.50">
          <HStack justify="space-between" align="center" mb={3}>
            <Text fontWeight="bold" color="#5b182b">Datos generales</Text>
            <Badge colorScheme={form.estatus === 'activo' ? 'green' : 'gray'}>{form.estatus || 'activo'}</Badge>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Nombre del atractivo</FormLabel>
              <Input
                value={form.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                isDisabled={disabled}
                placeholder="Ej. Zona arqueológica, mirador, parque"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Tipo</FormLabel>
              <Select
                value={form.tipo}
                onChange={(e) => handleChange('tipo', e.target.value)}
                isDisabled={disabled}
                placeholder="Selecciona tipo"
              >
                <option value="Natural">Natural</option>
                <option value="Cultural">Cultural</option>
                <option value="Religioso">Religioso</option>
                <option value="Gastronómico">Gastronómico</option>
                <option value="Recreativo">Recreativo</option>
                <option value="Histórico">Histórico</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Comunidad</FormLabel>
              <Input
                value={form.comunidad}
                onChange={(e) => handleChange('comunidad', e.target.value)}
                isDisabled={disabled}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Estatus</FormLabel>
              <Select
                value={form.estatus}
                onChange={(e) => handleChange('estatus', e.target.value)}
                isDisabled={disabled}
              >
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </Select>
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Descripción</FormLabel>
              <Textarea
                value={form.descripcion}
                onChange={(e) => handleChange('descripcion', e.target.value)}
                isDisabled={disabled}
                rows={4}
                placeholder="Describe el atractivo, actividades y características"
              />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4}>
          <Text fontWeight="bold" color="#5b182b" mb={3}>Ubicación (Google Maps)</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
            <FormControl>
              <FormLabel>Ubicación / dirección</FormLabel>
              <Input
                value={form.ubicacion}
                onChange={(e) => handleChange('ubicacion', e.target.value)}
                isDisabled={disabled}
                placeholder="Escribe la ubicación y usa Buscar en mapa"
              />
              <FormHelperText>También puedes seleccionar el punto directamente en el mapa.</FormHelperText>
            </FormControl>
            <HStack align="end" spacing={3}>
              {!disabled && (
                <Button onClick={geocodeDireccion} variant="outline" color="#5b182b" borderColor="#5b182b" _hover={{ bg: '#f7efe8' }} flex="1">
                  Buscar en mapa
                </Button>
              )}
              {!disabled && (
                <Button onClick={usarUbicacionActual} variant="outline" flex="1">
                  Mi ubicación
                </Button>
              )}
            </HStack>
            <FormControl>
              <FormLabel>Latitud</FormLabel>
              <Input
                type="number"
                step="0.00000001"
                value={form.lat}
                onChange={(e) => handleChange('lat', e.target.value)}
                isDisabled={disabled}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Longitud</FormLabel>
              <Input
                type="number"
                step="0.00000001"
                value={form.lng}
                onChange={(e) => handleChange('lng', e.target.value)}
                isDisabled={disabled}
              />
            </FormControl>
          </SimpleGrid>

          <Box borderWidth="1px" borderRadius="md" overflow="hidden" h="320px" bg="gray.100">
            {loadError ? (
              <Box p={4}>
                <Text color="red.500">No fue posible cargar Google Maps.</Text>
              </Box>
            ) : !GOOGLE_MAPS_API_KEY ? (
              <Box p={4}>
                <Text color="orange.500">Falta la API key de Google Maps.</Text>
              </Box>
            ) : mapaCargado ? (
              <GoogleMap
                zoom={markerPosition ? 16 : 13}
                center={markerPosition || mapCenter}
                mapContainerStyle={{ width: '100%', height: '100%' }}
                onClick={handleMapClick}
                options={{ streetViewControl: false, mapTypeControl: false }}
              >
                {markerPosition && <Marker position={markerPosition} />}
              </GoogleMap>
            ) : (
              <Box p={4}>
                <Text color="gray.500">Cargando mapa...</Text>
              </Box>
            )}
          </Box>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4} bg="gray.50">
          <Text fontWeight="bold" color="#5b182b" mb={3}>Horarios y operación</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Días de atención</FormLabel>
              <Select
                value={form.horarioDias}
                onChange={(e) => handleChange('horarioDias', e.target.value)}
                isDisabled={disabled}
                placeholder="Selecciona una opción"
              >
                <option value="Lunes a viernes">Lunes a viernes</option>
                <option value="Lunes a sábado">Lunes a sábado</option>
                <option value="Martes a domingo">Martes a domingo</option>
                <option value="Todos los días">Todos los días</option>
                <option value="Fines de semana">Fines de semana</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Rango de horario</FormLabel>
              <HStack>
                <Input
                  type="time"
                  value={form.horaApertura}
                  onChange={(e) => handleChange('horaApertura', e.target.value)}
                  isDisabled={disabled}
                />
                <Text color="gray.500">a</Text>
                <Input
                  type="time"
                  value={form.horaCierre}
                  onChange={(e) => handleChange('horaCierre', e.target.value)}
                  isDisabled={disabled}
                />
              </HStack>
            </FormControl>
            <FormControl>
              <FormLabel>Costo</FormLabel>
              <Input
                value={form.costo}
                onChange={(e) => handleChange('costo', e.target.value)}
                isDisabled={disabled}
                placeholder="Ej. Entrada libre / $50 adultos"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Contacto</FormLabel>
              <Input
                value={form.contacto}
                onChange={(e) => handleChange('contacto', e.target.value)}
                isDisabled={disabled}
                placeholder="Teléfono, WhatsApp o responsable"
              />
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Notas de horario / reglas</FormLabel>
              <Textarea
                value={form.horarioNotas}
                onChange={(e) => handleChange('horarioNotas', e.target.value)}
                isDisabled={disabled}
                rows={2}
                placeholder="Ej. Cerrado en días festivos, acceso con cita"
              />
              {horarioPreview && (
                <FormHelperText>
                  Se guardará como: <strong>{horarioPreview}</strong>
                </FormHelperText>
              )}
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Servicios disponibles</FormLabel>
              <Textarea
                value={form.servicios}
                onChange={(e) => handleChange('servicios', e.target.value)}
                isDisabled={disabled}
                rows={3}
                placeholder="Ej. Sanitarios, estacionamiento, guías, alimentos"
              />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4}>
          <Text fontWeight="bold" color="#5b182b" mb={3}>Fotos del atractivo (archivo)</Text>
          {!disabled && (
            <FormControl mb={4}>
              <FormLabel>Subir fotos</FormLabel>
              <Input type="file" accept="image/*" multiple onChange={handleFotosSeleccionadas} p={1} />
              <FormHelperText>Máximo sugerido: 10 MB por archivo.</FormHelperText>
            </FormControl>
          )}

          <Stack spacing={3}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>Fotos registradas</Text>
              {fotosExistentes.length === 0 ? (
                <Text color="gray.500" fontSize="sm">Sin fotos registradas.</Text>
              ) : (
                <Wrap>
                  {fotosExistentes.map((foto) => (
                    <WrapItem key={foto}>
                      <Tag size="md" colorScheme="green" borderRadius="full">
                        <TagLabel>
                          <Link href={resolveAssetUrl(foto)} isExternal>
                            {foto.split('/').pop()}
                          </Link>
                        </TagLabel>
                        {!disabled && <TagCloseButton onClick={() => removeFotoExistente(foto)} />}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </Box>

            {fotosExistentes.filter(isImagePath).length > 0 && (
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                {fotosExistentes.filter(isImagePath).map((foto) => (
                  <Box key={`${foto}-preview`} borderWidth="1px" borderRadius="md" overflow="hidden">
                    <Image src={resolveAssetUrl(foto)} alt={foto} h="120px" w="100%" objectFit="cover" />
                  </Box>
                ))}
              </SimpleGrid>
            )}

            <Divider />

            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>Nuevas fotos por cargar</Text>
              {fotosNuevas.length === 0 ? (
                <Text color="gray.500" fontSize="sm">No has seleccionado archivos nuevos.</Text>
              ) : (
                <Wrap>
                  {fotosNuevas.map((file, index) => (
                    <WrapItem key={`${file.name}-${index}`}>
                      <Tag size="md" colorScheme="blue" borderRadius="full">
                        <TagLabel>{file.name}</TagLabel>
                        {!disabled && <TagCloseButton onClick={() => removeFotoNueva(index)} />}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </Box>
          </Stack>
        </Box>

        {error && (
          <Box borderWidth="1px" borderColor="red.200" bg="red.50" borderRadius="md" p={3}>
            <Text color="red.600" fontSize="sm">{error}</Text>
          </Box>
        )}
      </Stack>

      {modo !== 'view' && (
        <Box mt={6} display="flex" gap={3} justifyContent="flex-end">
          <Button onClick={onCancelar} variant="outline">Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubmit} isLoading={guardando} loadingText="Guardando">
            Guardar
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FormularioAtractivo;
